/**
 * ParentTaskSelector - Seletor de tarefa pai com busca via RPC
 * 
 * Permite vincular uma tarefa como subtarefa de outra.
 * Exclui a própria tarefa da listagem para evitar referência circular.
 * 
 * @version 2.0.0
 * RELEASE: 2026-04-07 v2.0.0 - Migração para RPC server-side
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronsUpDown, GitBranch } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useTaskModuleType } from '@/hooks/useTaskModuleType';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

/** Interface da tarefa para listagem */
interface TaskOption {
  id: string;
  number: number;
  title: string;
  /** Prazo da tarefa (para validação de subtarefa) */
  due_date: string | null;
}

interface ParentTaskSelectorProps {
  value?: string | null;
  onSelect: (taskId: string | null) => void;
  /** Callback com dados completos da tarefa selecionada (inclui due_date) */
  onTaskData?: (task: TaskOption | null) => void;
  /** ID da tarefa atual (para excluir da lista) */
  excludeTaskId?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Trunca o título para tamanho fixo, evitando quebra de layout no modal.
 * @param title - Título original da tarefa
 * @param maxLength - Limite máximo de caracteres (padrão: 50)
 */
function truncateTitle(title: string, maxLength = 50): string {
  if (title.length <= maxLength) return title;
  return `${title.slice(0, maxLength)}…`;
}

/**
 * Seletor de tarefa pai com busca por título ou número via RPC server-side
 */
export function ParentTaskSelector({
  value,
  onSelect,
  onTaskData,
  excludeTaskId,
  placeholder = 'Selecionar tarefa pai...',
  disabled = false,
  className,
}: ParentTaskSelectorProps) {
  const { currentTenant } = useAuth();
  // Módulo corrente (normal | retention | legal) derivado da rota
  const taskType = useTaskModuleType();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Busca tarefas via RPC server-side
  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ['tasks-selector', currentTenant?.id, search, excludeTaskId, taskType],
    queryFn: async () => {
      if (!currentTenant?.id) return [];
      const { data, error } = await supabase.rpc('search_tasks_for_selector', {
        p_search: search || null,
        p_exclude_task_id: excludeTaskId || null,
        p_tenant_hint: currentTenant.id,
        // Sugere apenas tarefas do mesmo módulo (Tarefas / Retenção / Jurídico)
        p_task_type: taskType,
      } as any);
      if (error) throw error;
      return (data || []) as TaskOption[];
    },
    enabled: !!currentTenant?.id && open,
  });

  // Busca tarefa selecionada via RPC (para exibição)
  const { data: selectedTask } = useQuery({
    queryKey: ['task-selected', value],
    queryFn: async () => {
      if (!value) return null;
      const { data, error } = await supabase.rpc('search_tasks_for_selector', {
        p_task_id: value,
      });
      if (error || !data || data.length === 0) return null;
      return data[0] as TaskOption;
    },
    enabled: !!value,
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('w-full max-w-full justify-between overflow-hidden', className)}
        >
          {/* Container com restrição para truncamento */}
          <span className="flex items-center gap-2 min-w-0 overflow-hidden flex-1">
            <GitBranch className="h-4 w-4 text-muted-foreground shrink-0" />
            <span
              className={cn('truncate block', !selectedTask && 'text-muted-foreground')}
              title={selectedTask ? `#${selectedTask.number} ${selectedTask.title}` : undefined}
            >
              {selectedTask
                ? `#${selectedTask.number} ${truncateTitle(selectedTask.title)}`
                : placeholder}
            </span>
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar por título ou número..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>
              {isLoading ? 'Buscando...' : 'Nenhuma tarefa encontrada.'}
            </CommandEmpty>
            <CommandGroup>
              {/* Opção para limpar seleção */}
              {value && (
                <CommandItem
                  value="clear"
                  onSelect={() => { onSelect(null); onTaskData?.(null); setOpen(false); }}
                  className="text-muted-foreground"
                >
                  <GitBranch className="mr-2 h-4 w-4" />
                  Nenhuma tarefa pai
                </CommandItem>
              )}
              {tasks.map((task) => (
                <CommandItem
                  key={task.id}
                  value={task.id}
                  onSelect={() => { onSelect(task.id); onTaskData?.(task); setOpen(false); }}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <GitBranch className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="font-mono text-xs text-muted-foreground shrink-0">#{task.number}</span>
                    <span className="truncate font-medium">{task.title}</span>
                  </div>
                  <Check
                    className={cn('ml-2 h-4 w-4', value === task.id ? 'opacity-100' : 'opacity-0')}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
